import React from 'react';
import { Anchor } from 'lucide-react';
import type { ValueStreamRow } from '../utils/programmeModel';
import ProvenanceBadge from './ProvenanceBadge';
import '../styles/ValueStreamTable.css';

// ValueStreamTable — the value-stream table of the value-proven board
// (029-value-proven-dashboard, Increment D; User Story 3, FR-009/FR-015/FR-016,
// SC-001/SC-005/SC-008). A PURE view over the model's `streamRows` array: it
// renders EXACTLY the ValueStreamRow objects `deriveValueProven(...).streamRows`
// hands it, ONE data row per input row, in order. It does NOT fetch, derive, or
// read the snapshot — it binds what it is GIVEN.
//
// Honesty contract:
//   • REAL vs ILLUSTRATIVE legible at a glance (FR-009/SC-001): the REAL stream
//     NAME column carries NO badge; the illustrative loop columns are covered by
//     ONE table-level ProvenanceBadge in the <caption> (not per-cell/per-row), so
//     the accessible name "Illustrative sample data" appears EXACTLY once.
//   • Wedge marker (FR-009): the single row with isWedge renders the visible text
//     "Calibration wedge" — a labelled marker conveyed as TEXT, never colour alone.
//   • Honest empty cells (SC-005): a null loop-column value renders the em dash
//     "—" (U+2014), NEVER a fabricated "0".
//   • Empty state: zero rows render an honest message naming the absence, NOT a
//     fabricated row and no faked figures.
//   • Reserved-colour (FR-016/SC-008): NEUTRAL chrome only (index.css tokens); the
//     table NEVER spends the reserved coral (--ap-accent).
//   • a11y (FR-015): a semantic <table> with <th scope> column headers; lucide
//     glyphs decorative (aria-hidden); state carried by text, never colour alone.

// The honest no-value glyph an un-sized cell MUST render (SC-005): the EM DASH
// (U+2014), never £0/0.
const EM_DASH = '—';

// Render a nullable loop-column value honestly: the em dash for null, otherwise
// the value's own string form (numbers/ranges as given).
function cell(value: string | number | null): string {
  return value === null ? EM_DASH : String(value);
}

const ValueStreamTable: React.FC<{ rows: ValueStreamRow[] }> = ({ rows }) => {
  if (rows.length === 0) {
    return (
      <p className="ap-value-stream-empty">No value streams yet.</p>
    );
  }

  return (
    <table className="ap-value-stream-table">
      <caption className="ap-value-stream-caption">
        <span className="ap-value-stream-caption-label">Value streams</span>
        {/* ONE table-level illustrative badge covering the loop columns (FR-009/
            SC-001): not per-cell/per-row, so "Illustrative sample data" appears
            exactly once. The REAL stream-name column is NOT badged. */}
        <ProvenanceBadge source="illustrative" />
      </caption>
      <thead>
        <tr>
          <th scope="col">Value stream</th>
          <th scope="col">Stage</th>
          <th scope="col">Baseline</th>
          <th scope="col">Predicted</th>
          <th scope="col">Delivery</th>
          <th scope="col">Realised</th>
          <th scope="col">Adoption</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <th scope="row" className="ap-value-stream-name">
              <span>{row.name}</span>
              {row.isWedge && (
                <span className="ap-value-stream-wedge">
                  <Anchor className="ap-value-stream-wedge-icon" size={12} aria-hidden="true" />
                  Calibration wedge
                </span>
              )}
            </th>
            <td>{cell(row.columns.stage)}</td>
            <td>{cell(row.columns.baseline)}</td>
            <td>{cell(row.columns.predictedRange)}</td>
            <td>{cell(row.columns.delivery)}</td>
            <td>{cell(row.columns.realised)}</td>
            <td>{cell(row.columns.adoption)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default ValueStreamTable;
